import React from "react";
import styled from "@emotion/styled";
import { useSelector } from "react-redux";
import { ICellRendererParams } from "ag-grid-community";
import { isProgressRow, selectStatusCountsEntry } from "../../store/statusCountsSlice";
import { RagMarker } from "./RagMarker";
import { ragBand } from "./ragBand";
import { markerLabel } from "./statusProgress";

const Cell = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 100%;
`;

const StatusText = styled.span`
  min-width: 62px;
`;

/**
 * Status text plus, for Waiting / Notified / Failed leaf rows, the key's progress marker.
 * Subscribes to its own key, so it re-renders when that key's counts arrive.
 */
export const StatusCellRenderer = (params: ICellRendererParams): JSX.Element => {
  const data = params.data as { id?: string; partitionKey?: string; status?: unknown } | undefined;
  const decorated = !params.node?.group && isProgressRow(data);
  const entry = useSelector(selectStatusCountsEntry(decorated ? data?.partitionKey : undefined));
  const text = String(params.valueFormatted ?? params.value ?? "");

  if (!decorated || !entry || entry.state === "error") {
    return <Cell>{text}</Cell>;
  }

  if (entry.state === "loading") {
    return (
      <Cell>
        <StatusText>{text}</StatusText>
        <RagMarker band="loading" label={markerLabel(entry)} testId="rag-marker" />
      </Cell>
    );
  }

  const band = ragBand(entry.data, entry.asOf);

  return (
    <Cell>
      <StatusText>{text}</StatusText>
      {band !== "none" && <RagMarker band={band} label={markerLabel(entry)} testId="rag-marker" />}
    </Cell>
  );
};
